'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'

interface NoticeItem {
  id: string
  title: string
  content: string
  is_pinned: boolean
  badge?: string
  created_at: string
}

export default function NoticePage() {
  const { theme } = useTheme()
  const [notices, setNotices] = useState<NoticeItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadNotices()
  }, [])

  const loadNotices = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) throw error
      setNotices(data || [])
    } catch (err) {
      console.error('공지사항 로드 실패:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '-').replace('.', '')
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b py-4 xxs:py-2 xs:py-3 px-3 xxs:px-1.5 xs:px-2 ${
      theme === 'light'
        ? 'from-purple-50 to-white'
        : 'from-gray-900 to-gray-800'
    }`}>
      <div className="max-w-6xl mx-auto">
        <Link href="/community" className={`hover:underline mb-2 xxs:mb-1.5 text-xs xxs:text-[10px] inline-block ${
          theme === 'light' ? 'text-blue-600' : 'text-blue-400'
        }`}>
          ← 커뮤니티
        </Link>

        <h1 className={`text-xl xxs:text-sm xs:text-base font-bold mb-1.5 xxs:mb-1 ${
          theme === 'light' ? 'text-gray-900' : 'text-white'
        }`}>공지사항</h1>
        <p className={`text-xs xxs:text-[10px] mb-3 xxs:mb-2 ${
          theme === 'light' ? 'text-gray-800' : 'text-gray-300'
        }`}>
          오늘다이빙 서비스 공지사항입니다.
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <p className={`text-xs ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>로딩 중...</p>
          </div>
        ) : notices.length === 0 ? (
          <div className={`rounded-lg p-6 text-center ${theme === 'light' ? 'bg-white' : 'bg-gray-800'}`}>
            <p className={`text-xs ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
              공지사항이 없습니다.
            </p>
          </div>
        ) : (
          <div className="space-y-2 xxs:space-y-1.5">
            {notices.map((notice) => (
              <Link
                key={notice.id}
                href={`/community/notice/${notice.id}`}
                className={`block rounded-md xxs:rounded shadow-md p-3 xxs:p-2 hover:shadow-lg transition-all cursor-pointer ${
                  notice.is_pinned
                    ? theme === 'light'
                      ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500'
                      : 'bg-blue-900/20 hover:bg-blue-900/30 border-l-4 border-blue-500'
                    : theme === 'light'
                      ? 'bg-white hover:bg-gray-50'
                      : 'bg-gray-800 hover:bg-gray-750'
                }`}
              >
                <div className="flex items-start justify-between mb-1 xxs:mb-0.5 gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {notice.badge && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded flex-shrink-0">
                        {notice.badge}
                      </span>
                    )}
                    <h3 className={`text-sm xxs:text-[11px] font-bold leading-tight ${
                      theme === 'light' ? 'text-gray-900' : 'text-white'
                    }`}>{notice.title}</h3>
                  </div>
                  <span className={`text-[10px] xxs:text-[9px] whitespace-nowrap flex-shrink-0 ${
                    theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  }`}>{formatDate(notice.created_at)}</span>
                </div>
                <p className={`text-xs xxs:text-[10px] leading-relaxed line-clamp-2 ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>{notice.content}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
